import { __decorate } from "tslib";
import React, { cloneElement, Component, isValidElement } from 'react';
import PropTypes from 'prop-types';
import { action, observable } from 'mobx';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import omit from 'lodash/omit';
import isString from 'lodash/isString';
import debounce from 'lodash/debounce';
import defaultTo from 'lodash/defaultTo';
import classes from 'component-classes';
import { pxToRem } from '../../../lib/_util/UnitConvertor';
import ReactResizeObserver from '../../../lib/_util/resizeObserver';
import raf from 'raf';
import { minColumnWidth } from './Column';
import TableContext from './TableContext';
import Icon from '../icon';
import EventManager from '../_util/EventManager';
import { getAlignByField, getColumnKey, getColumnLock, getHeader, getPlacementByAlign, isStickySupport } from './utils';
import { ColumnAlign, TableColumnTooltip } from './enum';
import { ShowHelp } from '../field/enum';
import Tooltip from '../tooltip/Tooltip';
import autobind from '../_util/autobind';
let TableHeaderCell = class TableHeaderCell extends Component {
    constructor() {
        super(...arguments);
        this.resizeEvent = new EventManager(typeof window !== 'undefined' && document);
        this.resizeBoundary = 0;
        this.delayResizeStart = debounce((e) => {
            this.resizeStart(e);
        }, 300);
    }
    saveElement(element) {
        this.element = element;
    }
    handleClick() {
        const { column, dataSet } = this.props;
        const { name } = column;
        if (name) {
            dataSet.sort(name);
        }
    }
    getNode(column) {
        const { getHeaderNode } = this.props;
        const headerDom = getHeaderNode();
        if (headerDom) {
            return headerDom.querySelector(`[data-index="${getColumnKey(column)}"]`);
        }
    }
    setResizeColumn(column) {
        this.resizeColumn = column;
        const node = this.getNode(column);
        if (node) {
            this.resizeBoundary = node.getBoundingClientRect().left;
        }
    }
    handleLeftResize(e) {
        const { prevColumn } = this.props;
        const { tableStore: { props: { autoMaxWidth } } } = this.context;
        this.setResizeColumn(prevColumn);
        if (autoMaxWidth) {
            e.persist();
            this.delayResizeStart(e);
        }
        else {
            this.resizeStart(e);
        }
    }
    handleStopResize() {
        if (this.delayResizeStart) {
            this.delayResizeStart.cancel();
        }
    }
    handleRightResize(e) {
        const { resizeColumn } = this.props;
        const { tableStore: { props: { autoMaxWidth } } } = this.context;
        this.setResizeColumn(resizeColumn);
        if (autoMaxWidth) {
            e.persist();
            this.delayResizeStart(e);
        }
        else {
            this.resizeStart(e);
        }
    }
    handleLeftDoubleClick(_e) {
        if (this.delayResizeStart) {
            this.delayResizeStart.cancel();
            this.resizeDoubleClick();
        }
    }
    handleRightDoubleClick(_e) {
        if (this.delayResizeStart) {
            this.delayResizeStart.cancel();
            this.resizeDoubleClick();
        }
    }
    resizeDoubleClick() {
        const column = this.resizeColumn;
        const { tableStore } = this.context;
        const { prefixCls, node: { element } } = tableStore;
        if (column) {
            const maxWidth = Math.max(...[
                ...element.querySelectorAll(`td[data-index=${getColumnKey(column)}] > .${prefixCls}-cell-inner`),
            ].map(({ clientWidth, scrollWidth, parentNode: { offsetWidth } }) => offsetWidth + scrollWidth - clientWidth + 1), minColumnWidth(column), column.width ? column.width : 0);
            if (maxWidth !== column.width) {
                tableStore.changeCustomizedColumnValue(column, {
                    width: maxWidth,
                });
            }
            else if (column.minWidth) {
                tableStore.changeCustomizedColumnValue(column, {
                    width: column.minWidth,
                });
            }
        }
    }
    resizeStart(e) {
        const { tableStore, } = this.context;
        const { prefixCls, node: { element } } = tableStore;
        tableStore.columnResizing = true;
        classes(element).add(`${prefixCls}-resizing`);
        delete this.resizePosition;
        this.setSplitLinePosition(e.pageX);
        this.resizeEvent
            .addEventListener('mousemove', this.resize)
            .addEventListener('mouseup', this.resizeEnd);
    }
    resize(e) {
        const column = this.resizeColumn;
        const limit = this.resizeBoundary + minColumnWidth(column);
        let left = e.pageX;
        if (left < limit) {
            left = limit;
        }
        this.resizePosition = this.setSplitLinePosition(left);
    }
    resizeEnd() {
        const { tableStore, } = this.context;
        const { prefixCls, node: { element }, } = tableStore;
        tableStore.columnResizing = false;
        classes(element).remove(`${prefixCls}-resizing`);
        this.resizeEvent.removeEventListener('mousemove').removeEventListener('mouseup');
        const column = this.resizeColumn;
        if (this.resizePosition && column) {
            const newWidth = Math.round(Math.max(this.resizePosition - this.resizeBoundary, minColumnWidth(column)));
            if (newWidth !== column.width) {
                tableStore.changeCustomizedColumnValue(column, {
                    width: newWidth,
                });
            }
        }
    }
    setSplitLinePosition(left) {
        const { tableStore: { node: { resizeLine }, }, } = this.context;
        const { left: rectLeft } = resizeLine.offsetParent.getBoundingClientRect();
        left -= rectLeft;
        if (left < 0) {
            left = 0;
        }
        resizeLine.style.left = pxToRem(left) || null;
        return left + rectLeft;
    }
    renderResizer() {
        const { prevColumn, column } = this.props;
        const { tableStore: { prefixCls, props: { autoMaxWidth } } } = this.context;
        const resizerPrefixCls = `${prefixCls}-resizer`;
        const pre = prevColumn && prevColumn.resizable && (React.createElement("div", { key: "pre", className: `${resizerPrefixCls} ${resizerPrefixCls}-left`, onDoubleClick: autoMaxWidth ? this.handleLeftDoubleClick : undefined, onMouseDown: this.handleLeftResize, onMouseUp: autoMaxWidth ? this.handleStopResize : undefined }));
        const next = column.resizable && (React.createElement("div", { key: "next", className: `${resizerPrefixCls} ${resizerPrefixCls}-right`, onDoubleClick: autoMaxWidth ? this.handleRightDoubleClick : undefined, onMouseDown: this.handleRightResize, onMouseUp: autoMaxWidth ? this.handleStopResize : undefined }));
        return [pre, next];
    }
    handleResize() {
        const { element } = this;
        const { tableStore } = this.context;
        if (element && !tableStore.hidden) {
            if (this.nextFrameActionId !== undefined) {
                raf.cancel(this.nextFrameActionId);
            }
            this.nextFrameActionId = raf(this.syncSize);
        }
    }
    syncSize() {
        this.overflow = this.computeOverFlow();
    }
    computeOverFlow() {
        const { element } = this;
        if (element && element.textContent) {
            const { column } = this.props;
            const { tableStore } = this.context;
            if (tableStore.getColumnTooltip(column) === TableColumnTooltip.overflow) {
                const { clientWidth, scrollWidth } = element;
                return scrollWidth > clientWidth;
            }
        }
        return false;
    }
    getHelpIcon(field) {
        const { column } = this.props;
        const { tableStore: { prefixCls } } = this.context;
        const { help, showHelp, } = column;
        if (showHelp !== ShowHelp.none) {
            const fieldHelp = defaultTo(field && field.get('help'), help);
            if (fieldHelp) {
                return (React.createElement(Tooltip, { title: fieldHelp, placement: "bottom", key: "help" },
                    React.createElement(Icon, { type: "help_outline", className: `${prefixCls}-help-icon` })));
            }
        }
    }
    getSortIcon() {
        const { column } = this.props;
        const { tableStore: { prefixCls } } = this.context;
        const { sortable, name, } = column;
        if (sortable && name) {
            return React.createElement(Icon, { key: "sort", type: "arrow_upward", className: `${prefixCls}-sort-icon` });
        }
    }
    render() {
        const { column, dataSet, rowSpan, colSpan, style, className } = this.props;
        const { tableStore } = this.context;
        const { prefixCls, rowHeight, columnResizable, } = tableStore;
        const { headerClassName, headerStyle = {}, name, align, children, command, lock, } = column;
        const tooltip = tableStore.getColumnTooltip(column);
        const columnKey = getColumnKey(column);
        const columnLock = isStickySupport() && tableStore.overflowX && getColumnLock(lock);
        const classList = [`${prefixCls}-cell`];
        if (columnLock) {
            classList.push(`${prefixCls}-cell-fix-${columnLock}`);
        }
        if (className) {
            classList.push(className);
        }
        const field = dataSet.getField(name);
        if (headerClassName) {
            classList.push(headerClassName);
        }
        const cellStyle = {
            textAlign: align ||
                (command || (children && children.length) ? ColumnAlign.center : getAlignByField(field)),
            ...headerStyle,
            ...style,
        };
        const header = getHeader(column, dataSet);
        const headerNode = isValidElement(header) ? (cloneElement(header, { key: 'text' })) : isString(header) ? (React.createElement("span", { key: "text" }, header)) : (header);
        // 帮助按钮
        const helpIcon = this.getHelpIcon(field);
        // 排序按钮
        const sortIcon = this.getSortIcon();
        const childNodes = [
            headerNode,
        ];
        const innerProps = {
            className: classNames(`${prefixCls}-cell-inner`),
            children: childNodes,
            style: {},
        };
        if (helpIcon) {
            if (cellStyle.textAlign === ColumnAlign.right) {
                childNodes.unshift(helpIcon);
            }
            else {
                childNodes.push(helpIcon);
            }
        }
        if (sortIcon) {
            if (field && field.order) {
                classList.push(`${prefixCls}-sort-${field.order}`);
            }
            innerProps.onClick = this.handleClick;
            if (cellStyle.textAlign === ColumnAlign.right) {
                childNodes.unshift(sortIcon);
            }
            else {
                childNodes.push(sortIcon);
            }
        }
        if (rowHeight !== 'auto') {
            const height = Number(cellStyle.height) || (rowHeight * (rowSpan || 1));
            innerProps.style = {
                height: pxToRem(height),
                lineHeight: pxToRem(height - 2),
            };
        }
        if (tooltip === TableColumnTooltip.overflow) {
            innerProps.ref = this.saveElement;
        }
        const thProps = {
            className: classList.join(' '),
            rowSpan,
            colSpan,
            'data-index': columnKey,
            style: omit(cellStyle, ['width', 'height']),
        };
        const inner = (React.createElement("div", Object.assign({}, innerProps)));
        const th = (React.createElement("th", Object.assign({}, thProps),
            this.overflow || tooltip === TableColumnTooltip.always ?
                React.createElement(Tooltip, { key: "tooltip", title: header, placement: getPlacementByAlign(cellStyle.textAlign) }, inner) :
                inner,
            columnResizable && this.renderResizer()));
        return tooltip === TableColumnTooltip.overflow ? (React.createElement(ReactResizeObserver, { onResize: this.handleResize, resizeProp: "width" }, th)) : (th);
    }
    componentWillUnmount() {
        this.resizeEvent.clear();
        this.delayResizeStart.cancel();
    }
};
TableHeaderCell.displayName = 'TableHeaderCell';
TableHeaderCell.propTypes = {
    column: PropTypes.object.isRequired,
};
TableHeaderCell.contextType = TableContext;
__decorate([
    observable
], TableHeaderCell.prototype, "overflow", void 0);
__decorate([
    autobind
], TableHeaderCell.prototype, "saveElement", null);
__decorate([
    autobind
], TableHeaderCell.prototype, "handleClick", null);
__decorate([
    autobind
], TableHeaderCell.prototype, "handleLeftResize", null);
__decorate([
    autobind
], TableHeaderCell.prototype, "handleStopResize", null);
__decorate([
    autobind
], TableHeaderCell.prototype, "handleRightResize", null);
__decorate([
    autobind
], TableHeaderCell.prototype, "handleLeftDoubleClick", null);
__decorate([
    autobind
], TableHeaderCell.prototype, "handleRightDoubleClick", null);
__decorate([
    autobind,
    action
], TableHeaderCell.prototype, "resizeDoubleClick", null);
__decorate([
    action
], TableHeaderCell.prototype, "resizeStart", null);
__decorate([
    autobind
], TableHeaderCell.prototype, "resize", null);
__decorate([
    autobind,
    action
], TableHeaderCell.prototype, "resizeEnd", null);
__decorate([
    action
], TableHeaderCell.prototype, "setSplitLinePosition", null);
__decorate([
    autobind
], TableHeaderCell.prototype, "handleResize", null);
__decorate([
    autobind,
    action
], TableHeaderCell.prototype, "syncSize", null);
TableHeaderCell = __decorate([
    observer
], TableHeaderCell);
export default TableHeaderCell;
